import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import * as FormDialog from "../shared/FormDialog";
import { shareSharedProcessingMutationOptions } from "../api/sharedProcessings";

export const ShareSharedProcessingDialog = ({
  sharedProcessingId,
  organisations,
  onClose,
  onShared,
}: {
  sharedProcessingId: string;
  organisations: Array<{ organisationId: string; organisationName: string }>;
  onClose: () => void;
  onShared: () => void;
}) => {
  const { t } = useTranslation();
  const shareMutation = useMutation(shareSharedProcessingMutationOptions());
  const pluralSuffix = organisations.length > 1 ? "Plural" : "Singular";

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Form
        onSubmit={() =>
          shareMutation.mutate(
            {
              id: sharedProcessingId,
              organisationIds: organisations.map(organisation => organisation.organisationId),
            },
            { onSuccess: onShared }
          )
        }
      >
        <FormDialog.Title>
          {t(`SharedProcessing.Share.Dialog.Title${pluralSuffix}`, { numberOfOrganisations: organisations.length })}
        </FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={shareMutation.isError} />

          <Typography
            sx={{
              pb: 2,
              fontSize: theme => theme.typography.pxToRem(14),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {t(`SharedProcessing.Share.Dialog.Body${pluralSuffix}`)}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 3, gap: 0.5 }}>
            {organisations.map(organisation => (
              <Typography component="li" variant="body2" key={organisation.organisationId}>
                {organisation.organisationName}
              </Typography>
            ))}
          </Stack>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={shareMutation.isPending}>
            {t("SharedProcessing.Share.Dialog.ConfirmButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
